'use client';

const cache = new Map();
const inFlight = new Map();

const readCacheEntry = (key) => {
  const entry = cache.get(key);
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    cache.delete(key);
    return null;
  }
  return entry.value;
};

const writeCacheEntry = (key, value, ttlMs) => {
  cache.set(key, {
    value,
    expiresAt: Date.now() + ttlMs,
  });
};

export const CLIENT_CACHE_KEYS = {
  subscription: 'api:user:subscription',
  credits: 'api:user:credits',
  pricingPlans: 'api:pricing-plans',
  campaignConfigs: 'api:campaign-configs',
  draftsList: 'api:drafts:list',
  videoList: 'api:video:list',
  imageList: 'api:image:list',
};

export const CLIENT_CACHE_EVENTS = {
  subscriptionUpdated: 'client-cache:subscription-updated',
  creditsUpdated: 'client-cache:credits-updated',
  ownerAssetsUpdated: 'client-cache:owner-assets-updated',
};

const dispatchCacheEvent = (eventName) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(eventName));
};

export async function fetchCachedApi(url, { cacheKey = url, ttlMs = 30000, init = {}, force = false } = {}) {
  if (!force) {
    const cached = readCacheEntry(cacheKey);
    if (cached) return cached;
  }

  if (inFlight.has(cacheKey)) {
    return inFlight.get(cacheKey);
  }

  const mergedInit = { ...init };
  if (!('cache' in mergedInit)) {
    mergedInit.cache = 'no-store';
  }

  const requestPromise = fetch(url, mergedInit)
    .then(async (res) => {
      let data = null;
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        try {
          data = await res.json();
        } catch {
          data = null;
        }
      }

      const result = {
        ok: res.ok,
        status: res.status,
        data,
      };

      if (res.ok) {
        writeCacheEntry(cacheKey, result, ttlMs);
      }

      return result;
    })
    .finally(() => {
      inFlight.delete(cacheKey);
    });

  inFlight.set(cacheKey, requestPromise);
  return requestPromise;
}

export function invalidateClientCache(key) {
  cache.delete(key);
}

export function notifySubscriptionUpdated() {
  dispatchCacheEvent(CLIENT_CACHE_EVENTS.subscriptionUpdated);
}

export function notifyCreditsUpdated() {
  dispatchCacheEvent(CLIENT_CACHE_EVENTS.creditsUpdated);
}

export function clearClientCache() {
  cache.clear();
  inFlight.clear();
}
